import React, { useState } from 'react';
import { 
  Share2, 
  PlusCircle, 
  ArrowRight, 
  ShieldCheck, 
  KeyRound, 
  Sparkles, 
  Zap, 
  Lock, 
  RefreshCw, 
  Shield, 
  Activity, 
  CheckCircle2, 
  Globe, 
  X 
} from 'lucide-react';
import { generateRoomCode, generateAccessCode } from '../crypto';
import { CkLogo } from './CkLogo';

interface JoinRoomModalProps {
  onJoin: (roomId: string, accessCode: string) => void;
  onClose?: () => void;
  onOpenScanner?: () => void;
  onOpenDownloads?: () => void;
  onOpenGuide?: (tab?: 'android' | 'windows' | 'security') => void;
}

export const JoinRoomModal: React.FC<JoinRoomModalProps> = ({ 
  onJoin, 
  onClose,
  onOpenScanner,
  onOpenDownloads,
  onOpenGuide 
}) => {
  const [mode, setMode] = useState<'create' | 'join'>('create');
  const [roomIdInput, setRoomIdInput] = useState('');
  const [accessCodeInput, setAccessCodeInput] = useState('');

  const [generatedRoom, setGeneratedRoom] = useState(generateRoomCode());
  const [generatedAccess, setGeneratedAccess] = useState(generateAccessCode());

  const handleRefreshCodes = () => {
    setGeneratedRoom(generateRoomCode());
    setGeneratedAccess(generateAccessCode());
  };

  const handleCreate = () => {
    onJoin(generatedRoom, generatedAccess);
  };

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomIdInput.trim() || !accessCodeInput.trim()) return;
    onJoin(roomIdInput.trim().toUpperCase(), accessCodeInput.trim());
  };

  return (
    <div className="relative w-full max-w-xl mx-auto my-auto p-2">
      {/* Dynamic Ambient Backlight */}
      <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500/20 via-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl opacity-60 animate-pulse"></div>

      <div className="relative glass-card p-6 sm:p-9 border border-white/10 shadow-2xl">
        
        {/* Prominent Top-Right Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2.5 rounded-2xl bg-white/5 hover:bg-white/15 text-gray-400 hover:text-white border border-white/10 hover:border-white/20 transition-all hover:scale-110 active:scale-95 shadow-lg"
            title="Close modal"
          >
            <X size={20} />
          </button>
        )}

        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <CkLogo size={80} className="shadow-2xl shadow-cyan-500/35" />
            <span className="absolute -top-1 -right-1 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-cyan-500 shadow-glow-cyan"></span>
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white font-display">
            CK <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">Share</span>
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-2 max-w-md mx-auto leading-relaxed">
            Enterprise-grade real-time clipboard & file sync across Android and PC with zero server-side exposure.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex rounded-2xl bg-[#080A12] p-1.5 border border-white/10 mb-7 shadow-inner">
          <button
            type="button"
            onClick={() => setMode('create')}
            className={`flex-1 py-3 text-xs sm:text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${
              mode === 'create'
                ? 'bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 text-black shadow-lg shadow-cyan-500/25 font-extrabold'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <PlusCircle size={16} />
            <span>Create Mesh Room</span>
          </button>
          <button
            type="button"
            onClick={() => setMode('join')}
            className={`flex-1 py-3 text-xs sm:text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${
              mode === 'join'
                ? 'bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 text-black shadow-lg shadow-cyan-500/25 font-extrabold'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <KeyRound size={16} />
            <span>Join Existing Room</span>
          </button>
        </div>

        {mode === 'create' ? (
          <div className="space-y-5">
            {/* Generated Room Credentials Box */}
            <div className="p-5 rounded-2xl bg-[#080912]/90 border border-cyan-500/30 shadow-inner relative overflow-hidden space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Zap size={13} className="text-cyan-400" />
                  Auto-Generated E2EE Mesh Credentials
                </span>
                <button
                  onClick={handleRefreshCodes}
                  className="text-slate-500 hover:text-cyan-400 transition-all p-1 rounded-lg hover:bg-white/5 active:rotate-180 duration-300"
                  title="Generate new codes"
                >
                  <RefreshCw size={14} />
                </button>
              </div>

              <div className="space-y-2.5">
                <div className="flex items-center justify-between p-3 rounded-xl bg-black/50 border border-white/5">
                  <div>
                    <span className="text-[11px] text-slate-400 block font-semibold">ROOM IDENTIFIER</span>
                    <span className="font-mono text-lg font-extrabold text-cyan-300 tracking-wider">
                      {generatedRoom}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono font-bold bg-cyan-950/60 text-cyan-300 border border-cyan-500/40 px-2 py-0.5 rounded-full">
                    Active Relay
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-black/50 border border-white/5">
                  <div>
                    <span className="text-[11px] text-slate-400 block font-semibold">PBKDF2 DERIVED ENCRYPTION KEY</span>
                    <span className="font-mono text-sm font-bold text-purple-300 tracking-wide">
                      {generatedAccess}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono font-bold bg-purple-950/60 text-purple-300 border border-purple-500/40 px-2 py-0.5 rounded-full">
                    AES-256
                  </span>
                </div>
              </div>
            </div>

            {/* Cryptographic Security Specs */}
            <div className="p-4 rounded-2xl bg-cyan-950/20 border border-cyan-500/20 space-y-2 text-xs text-slate-300">
              <div className="flex items-center gap-2 font-bold text-white">
                <ShieldCheck size={16} className="text-cyan-400 flex-shrink-0" />
                <span>Zero-Knowledge End-to-End Cryptographic Guarantee</span>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                All clipboards and dropped files are encrypted on-device before transmission. Relay servers only route opaque ciphertexts.
              </p>
            </div>

            {/* Launch Button */}
            <button onClick={handleCreate} className="btn-primary w-full py-3.5 text-sm font-bold shadow-xl shadow-cyan-500/30">
              <Sparkles size={17} />
              <span>Launch Encrypted Mesh</span>
            </button>
          </div>
        ) : (
          <form onSubmit={handleJoin} className="space-y-4">
            {onOpenScanner && (
              <button
                type="button"
                onClick={onOpenScanner}
                className="w-full py-3 rounded-2xl bg-cyan-950/40 border border-cyan-500/40 text-cyan-300 font-bold text-xs sm:text-sm flex items-center justify-center gap-2 hover:bg-cyan-900/50 shadow-glow-cyan transition-all mb-4"
              >
                <Sparkles size={16} className="text-cyan-400" />
                <span>📷 Scan PC Pairing QR Code</span>
              </button>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Zap size={13} className="text-cyan-400" />
                Room Identifier
              </label>
              <input
                type="text"
                required
                value={roomIdInput}
                onChange={(e) => setRoomIdInput(e.target.value)}
                placeholder="e.g. CK-9872"
                className="glass-input w-full uppercase font-mono text-sm tracking-wider"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Lock size={13} className="text-purple-400" />
                Room Access Key / Password
              </label>
              <input
                type="text"
                required
                value={accessCodeInput}
                onChange={(e) => setAccessCodeInput(e.target.value)}
                placeholder="Enter Access Key"
                className="glass-input w-full font-mono text-sm"
              />
            </div>

            <button type="submit" className="btn-primary w-full py-3.5 text-sm font-bold shadow-xl shadow-cyan-500/30 mt-2">
              <span>Connect to Mesh</span>
              <ArrowRight size={16} />
            </button>
          </form>
        )}

        {/* Quick App Downloads & Setup Guide Bar */}
        <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between flex-wrap gap-2 text-xs">
          {onOpenDownloads && (
            <button
              onClick={onOpenDownloads}
              className="flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 transition-colors font-semibold"
            >
              <span>📥 Download Android APK &amp; Windows</span>
            </button>
          )}

          {onOpenGuide && (
            <button
              onClick={() => onOpenGuide('android')}
              className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors"
            >
              <span>📖 Step-by-Step Guide</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
